/**
 * A world written down in the player's own browser.
 *
 * A world held in a tab used to live only in that tab: closing it was
 * closing the world, and everything anybody had built went with it. This is
 * what makes it come back - the host opens the page again, and the same
 * country is there with the same holes dug in it, the same bags, and the
 * same names claimed in it.
 *
 * **The awkward part is that storage is asynchronous and the game is not.**
 * `Keep` answers `read` straight away because a world being grown cannot
 * wait on a promise in the middle of itself. So this is a memory keep with
 * a real store behind it: everything is fetched once before the world is
 * built, reads are answered from that, and writes go to memory and are
 * pushed out afterwards. Exactly what the server does with files, only the
 * pushing out has to be asked for rather than being what `write` means.
 *
 * **Nothing is pushed out unless something changed.** A world nobody dug in
 * is a world that does not need writing, and rewriting eight megabytes
 * every half minute for a player standing still is somebody's disk.
 */
import type { Keep } from "../core/net/keep.js";
import { KEPT_FILES } from "../core/net/keep.js";
import type { SaveStore } from "./saveStore.js";

export interface BrowserKeep extends Keep {
  /** Push out whatever has changed. Nothing at all if nothing has. */
  flush(): Promise<void>;
  /** Whether anything is waiting to go out. */
  readonly dirty: boolean;
}

/**
 * Open a keep under that name, with whatever is already there in it.
 *
 * `under` is what separates one held world from another in the same
 * browser, and it has to be stable across sessions or coming back would
 * find an empty field. It is the world's name - which is the host's account
 * name, for exactly this reason.
 */
export async function browserKeep(
  store: SaveStore, under: string,
): Promise<BrowserKeep> {
  const held = new Map<string, Uint8Array>();
  const changed = new Set<string>();
  const path = (name: string): string => `${under}/${name}`;

  /*
   * Everything, before the world is built.
   *
   * One read each rather than one read of a bundle: they are written at
   * different moments for different reasons - the bags go whether or not
   * the ground moved - and a bundle would mean rewriting the world to
   * record that somebody crafted a spade.
   */
  for (const name of KEPT_FILES) {
    try {
      const got = await store.read(path(name));
      if (got !== null) held.set(name, new Uint8Array(got));
    } catch {
      /*
       * A file that will not come back is a file that was not there.
       *
       * The world grows fresh instead, which is a disappointment; refusing
       * to open at all because one of five things could not be read would
       * be worse, and the one most likely to fail - the world itself - is
       * also the one the seed can rebuild.
       */
    }
  }

  return {
    get dirty(): boolean { return changed.size > 0; },
    read: (name) => held.get(name) ?? null,
    write: (name, value) => {
      held.set(name, value);
      changed.add(name);
    },
    forget: () => {
      held.clear();
      changed.clear();
      for (const name of KEPT_FILES) {
        void store.remove(path(name)).catch(() => { /* already gone */ });
      }
    },
    async flush(): Promise<void> {
      const going = [...changed];
      changed.clear();
      for (const name of going) {
        const value = held.get(name);
        if (value === undefined) continue;
        try {
          /*
           * A copy of the bytes, not a view into somebody else's buffer.
           *
           * A Uint8Array from the encoder usually owns its whole buffer and
           * usually does not need this - and the one time it does not, what
           * gets written is the neighbouring file as well.
           */
          await store.write(path(name), value.slice().buffer);
        } catch {
          // Out of room, or a private window that refuses to keep anything.
          // Put it back: the next flush tries again, and a world that
          // cannot be written is still a world that can be played.
          changed.add(name);
        }
      }
    },
  };
}
